import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Target, Eye, BookOpen, Sparkles, TrendingUp, Users, Award, Leaf } from 'lucide-react';

const About = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const farmImages = [
    'https://images.unsplash.com/photo-1500382017468-9049fed747ef',
    'https://customer-assets.emergentagent.com/job_green-harvest-29/artifacts/pp3u0gkl_Livestock%20Farming.png',
    'https://customer-assets.emergentagent.com/job_green-harvest-29/artifacts/3ik4xw7h_Crop%20Cultivation.jpg',
    'https://customer-assets.emergentagent.com/job_green-harvest-29/artifacts/bp8mjkqi_Cashew%20Ecosystem.png',
    'https://images.unsplash.com/photo-1560493676-04071c5f467b',
  ];

  // Auto-rotate images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % farmImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="about-page">
      {/* Hero Section with Story */}
      <section className="hero-section relative h-screen flex items-center justify-center overflow-hidden">
        {/* Rotating Background Images */}
        {farmImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${image})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
            </div>
          </div>
        ))}
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
            From Tradition to Innovation
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 leading-relaxed animate-fade-in-delay">
            The story of how a traditional Nigerian farm embraced sustainable agriculture and modern technology to feed the future
          </p>
        </div>

        {/* Image Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
          {farmImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentImageIndex ? 'bg-white w-8' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Our Journey Section */}
      <section className="journey-section py-20 px-4 bg-gradient-to-b from-white to-green-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A transformation powered by vision, innovation, and commitment to sustainable agriculture
            </p>
          </div>

          {/* Story Timeline */}
          <div className="space-y-20">
            {/* Traditional Beginnings */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <div className="inline-block bg-amber-100 text-amber-700 px-4 py-2 rounded-full font-semibold mb-4">
                  The Beginning
                </div>
                <h3 className="text-3xl font-bold mb-6 text-gray-900">
                  Rooted in Tradition
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Daye Farm Ltd. began as a traditional Nigerian farm, with methods passed down through generations. 
                  Our founders understood the land, respected the seasons, and grew what the community needed.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  But they also saw the challenges: unpredictable yields, soil depletion, and the struggle to meet 
                  growing demand. They knew that to secure the future, tradition needed to meet innovation.
                </p>
              </div>
              <div className="h-96 rounded-2xl overflow-hidden shadow-2xl order-1 md:order-2">
                <img 
                  src="https://images.unsplash.com/photo-1574943320219-553eb213f72d" 
                  alt="Traditional farming" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* The Transformation */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449" 
                  alt="Innovation in farming" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full font-semibold mb-4">
                  The Transformation
                </div>
                <h3 className="text-3xl font-bold mb-6 text-gray-900">
                  Embracing Sustainable Innovation
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  The turning point came when we discovered integrated farming—a system where livestock, crops, and 
                  trees work together in harmony. We invested in learning, experimented with new methods, and slowly 
                  transformed our operations.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We introduced cattle grazing that naturally fertilized our fields. We planted cashew trees that 
                  provided shade for mushroom cultivation. Every decision was guided by one principle: work with 
                  nature, not against it.
                </p>
              </div>
            </div>

            {/* Modern Technology */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-semibold mb-4">
                  Technology Integration
                </div>
                <h3 className="text-3xl font-bold mb-6 text-gray-900">
                  Powered by Modern Innovation
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Today, Daye Farm Ltd. combines traditional wisdom with cutting-edge technology. We use precision 
                  agriculture techniques to monitor soil health, optimize irrigation, and predict optimal harvest times.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Our digital platform connects us directly with customers, reducing waste and ensuring fresh delivery. 
                  We track every aspect of our operations—from seed to sale—ensuring quality and sustainability at every step.
                </p>
                <div className="flex flex-wrap gap-3 mt-6">
                  <span className="bg-gray-100 px-4 py-2 rounded-full text-sm font-semibold">E-Commerce Platform</span>
                  <span className="bg-gray-100 px-4 py-2 rounded-full text-sm font-semibold">Digital Tracking</span>
                  <span className="bg-gray-100 px-4 py-2 rounded-full text-sm font-semibold">Smart Irrigation</span>
                  <span className="bg-gray-100 px-4 py-2 rounded-full text-sm font-semibold">Soil Monitoring</span>
                </div>
              </div>
              <div className="h-96 rounded-2xl overflow-hidden shadow-2xl order-1 md:order-2">
                <img 
                  src="https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8" 
                  alt="Technology in farming" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="mvv-section py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-green-100 overflow-hidden group">
              <div className="h-48 bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                <Target className="h-20 w-20 text-white" />
              </div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Our Mission</h3>
                <p className="text-gray-700 leading-relaxed">
                  To practice sustainable, innovative, and eco-friendly farming that nurtures both the land and our 
                  community, while leveraging modern technology to feed Nigeria's growing population.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-blue-100 overflow-hidden group">
              <div className="h-48 bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
                <Eye className="h-20 w-20 text-white" />
              </div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Our Vision</h3>
                <p className="text-gray-700 leading-relaxed">
                  To become Nigeria's leading example of integrated agriculture, where traditional farming wisdom meets 
                  modern innovation, creating a sustainable and profitable model for the future.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-amber-100 overflow-hidden group">
              <div className="h-48 bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Sparkles className="h-20 w-20 text-white" />
              </div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Our Values</h3>
                <p className="text-gray-700 leading-relaxed">
                  Sustainability, innovation, quality, and community. We believe in protecting the environment while 
                  using technology to create better opportunities for farmers and consumers alike.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Core Values Grid */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">What Drives Us</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-full mb-4">
                  <Leaf className="h-8 w-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Sustainability</h4>
                <p className="text-gray-700">Protecting our environment for future generations</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Innovation</h4>
                <p className="text-gray-700">Embracing technology and modern practices</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-600 rounded-full mb-4">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Quality</h4>
                <p className="text-gray-700">Maintaining highest standards in everything we do</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600 rounded-full mb-4">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Community</h4>
                <p className="text-gray-700">Supporting local farmers and economic growth</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="impact-section py-20 px-4 bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">Our Impact Today</h2>
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">50+</div>
              <p className="text-green-100">Hectares Farmed Sustainably</p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">100%</div>
              <p className="text-green-100">Organic Practices</p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">10+</div>
              <p className="text-green-100">Premium Products</p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">1000+</div>
              <p className="text-green-100">Satisfied Customers</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Future */}
      <section className="future-section py-20 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Building Tomorrow's Agriculture
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto mb-8">
            Our journey from traditional farming to sustainable innovation continues. With plans to expand our 
            mushroom cultivation, invest in agro-processing facilities, and empower more local farmers through 
            training and technology transfer, we're not just growing crops—we're cultivating the future of Nigerian agriculture.
          </p>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Expanding Operations</h4>
              <p className="text-gray-600">Scaling our integrated farming model across more land</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Technology Investment</h4>
              <p className="text-gray-600">Deploying more precision agriculture tools and systems</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Farmer Training</h4>
              <p className="text-gray-600">Sharing our knowledge with the farming community</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;